import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { downloadPatientFileApi, deletePatientFileApi } from '@/api/patientFiles';
import { sendFormApi } from '@/api/documents';
import { formatDocumentCardDescription } from '@/views/encounter-notes/utils/actionCardUtils';
import { buildPatientSession } from '@/utils/va-order-test/buildPatientSession';

/**
 * Composable for managing action cards (tasks, appointments, orders, documents)
 * in the encounter note editor.
 *
 * @param {Object} options
 * @param {import('vue').Ref} options.editorRef - Ref to EncounterEditor component
 * @param {import('vue').ComputedRef<string>} options.patientId - Current patient ID
 * @param {import('vue').Ref<object>} options.patientRecord - Current patient record ref
 * @param {import('vue').Ref<boolean>} options.isLoadingCalendar - Calendar loading state ref
 */
export function useActionCards({ editorRef, patientId, patientRecord, isLoadingCalendar }) {
  const store = useStore();
  const router = useRouter();

  // --- Dialog visibility ---
  const showCreateAppointmentDialog = ref(false);
  const showSendDocumentDialog = ref(false);
  const showNewOrderDialog = ref(false);
  const showDeleteTaskDialog = ref(false);
  const showCancelAppointmentDialog = ref(false);
  const showDeleteDocumentDialog = ref(false);
  const initialOrderItems = ref([]);

  const scheduleDetailActiveTab = ref('appointment');
  const taskToDelete = ref(null);
  const appointmentToCancel = ref(null);
  const documentToDelete = ref(null);

  const clinicId = computed(() => {
    const userInfo = store.state.login?.userInfo;
    return userInfo?.clinic_id ? String(userInfo.clinic_id) : '';
  });

  // --- Calendar data ---
  const fetchCalendarDataIfNeeded = async () => {
    const userInfo = store.state.login?.userInfo;
    const rawPatientList = store.state.calendar?.rawPatientList || [];
    const rawProviderList = store.state.calendar?.rawProviderList || [];
    const creatorCalendarId = store.state.calendar?.creatorCalendarId;

    if (!userInfo) return;

    isLoadingCalendar.value = true;
    try {
      if (rawPatientList.length === 0 || rawProviderList.length === 0) {
        if (userInfo.clinic_id) {
          await store.dispatch('calendar/fetchClinic', {
            clinicId: userInfo.clinic_id,
          });
        }
      }
      if (!creatorCalendarId) {
        await store.dispatch('calendar/getOrCreateCalendar', {
          calendar_owner_id: userInfo.customer_id,
          role: userInfo.user_roles?.includes('clinic') ? 'clinicadmin' : 'provider',
          practice_id: userInfo.clinic_id,
        });
      }
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    } finally {
      isLoadingCalendar.value = false;
    }
  };

  // --- Order patient session ---
  const setPatientSessionForOrder = () => {
    const patientData = buildPatientSession(patientRecord.value);
    if (!patientData) return;
    sessionStorage.setItem('patient', JSON.stringify(patientData));
    store.dispatch('orderTest/order/setPatient', patientData);
    store.dispatch('orderTest/user/setPatientCountry', patientData.country || '');
    store.dispatch('orderTest/setIsPatientInfoInSessionStorage');
  };

  // --- Slash commands ---
  const handleSlashCommand = async ({ type, itemId }) => {
    if (type === 'document') {
      showSendDocumentDialog.value = true;
      return;
    }
    if (type === 'order') {
      setPatientSessionForOrder();
      initialOrderItems.value = itemId ? [{ item_id: itemId, item_type: 'packagePriceId' }] : [];
      showNewOrderDialog.value = true;
      return;
    }
    if (type === 'appointment' || type === 'task') {
      await fetchCalendarDataIfNeeded();

      const userInfo = store.state.login?.userInfo;
      const rawPatientList = store.state.calendar?.rawPatientList || [];
      const rawProviderList = store.state.calendar?.rawProviderList || [];

      const participants = [];

      const patientIndex = rawPatientList.findIndex((p) => String(p.id) === String(patientId.value));
      if (patientIndex !== -1) {
        const patient = rawPatientList[patientIndex];
        if (patientIndex > 0) {
          const reorderedList = [
            patient,
            ...rawPatientList.slice(0, patientIndex),
            ...rawPatientList.slice(patientIndex + 1),
          ];
          store.commit('calendar/SET_RAW_PATIENT_LIST', reorderedList);
        }
        participants.push({
          participant_calendar_id: patient.calendar_id,
          role: 'patient',
        });
      }

      if (userInfo?.customer_id) {
        const provider = rawProviderList.find((p) => String(p.customer_id) === String(userInfo.customer_id));
        if (provider && provider.calendar_id) {
          participants.push({
            participant_calendar_id: provider.calendar_id,
            role: 'provider',
          });
        }
      }

      if (type === 'task') {
        const patient = rawPatientList.find((p) => String(p.id) === String(patientId.value));
        const taskInfo = patient?.calendar_id ? { calendar_id: patient.calendar_id } : null;
        store.dispatch('calendar/setCurrentTaskInfo', taskInfo);
      }

      store.dispatch('calendar/setCurrentEventInfo', {
        participants: participants.length > 0 ? participants : undefined,
      });

      scheduleDetailActiveTab.value = type;
      showCreateAppointmentDialog.value = true;
    }
  };

  // --- Appointment dialog ---
  const handleAppointmentDialogClose = () => {
    showCreateAppointmentDialog.value = false;
    store.dispatch('calendar/setCurrentEventInfo', null);
    if (scheduleDetailActiveTab.value === 'task') {
      store.dispatch('calendar/setCurrentTaskInfo', null);
    }
  };

  // --- Task CRUD ---
  const handleCreateTask = (taskResponse) => {
    if (taskResponse && editorRef.value?.insertCardAction) {
      const task = taskResponse.task || taskResponse;
      editorRef.value.insertCardAction({
        type: 'task',
        id: task.task_id || '',
        title: task.title || 'Untitled Task',
        description: task.description || 'No description provided.',
      });
    }
  };

  const handleUpdateTask = (taskResponse) => {
    const task = taskResponse?.task || taskResponse;
    if (task && task.task_id && editorRef.value?.updateCardAction) {
      editorRef.value.updateCardAction({
        type: 'task',
        id: task.task_id,
        title: task.title || 'Untitled Task',
        description: task.description || 'No description provided.',
      });
    }
  };

  const handleEditTask = async (taskData) => {
    await fetchCalendarDataIfNeeded();

    const rawPatientList = store.state.calendar?.rawPatientList || [];
    let calendarId = taskData.calendar_id;
    if (!calendarId) {
      const patient = rawPatientList.find((p) => String(p.id) === String(patientId.value));
      calendarId = patient?.calendar_id;
    }

    store.dispatch('calendar/setCurrentTaskInfo', {
      ...taskData,
      mode: 'edit',
      calendar_id: calendarId,
    });
    scheduleDetailActiveTab.value = 'task';
    showCreateAppointmentDialog.value = true;
  };

  const handleDeleteTask = (taskData) => {
    taskToDelete.value = taskData;
    showDeleteTaskDialog.value = true;
  };

  const handleDeleteTaskDialogClose = () => {
    showDeleteTaskDialog.value = false;
    taskToDelete.value = null;
  };

  const handleDeleteTaskConfirm = async () => {
    if (!taskToDelete.value?.task_id) {
      handleDeleteTaskDialogClose();
      return;
    }
    try {
      await store.dispatch('calendar/deleteTask', taskToDelete.value.task_id);
      if (editorRef.value?.removeCardAction) {
        editorRef.value.removeCardAction({
          type: 'task',
          id: taskToDelete.value.task_id,
        });
      }
    } catch (error) {
      console.error('Failed to delete task:', error);
    } finally {
      handleDeleteTaskDialogClose();
    }
  };

  // --- Appointment CRUD ---
  const formatAppointmentTime = (date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${hours}:${minutes}, ${month}-${day}-${year}`;
  };

  const buildAppointmentDescription = (event) => {
    const startTime = new Date(event.start_time);
    const endTime = new Date(event.end_time);
    const durationMinutes = Math.round((endTime - startTime) / (1000 * 60));
    const eventType = event.event_type || 'Appointment';
    return `${eventType}, ${durationMinutes} min | Appointment Time: ${formatAppointmentTime(startTime)}`;
  };

  const getAppointmentCalendarId = (event) =>
    event.creator_calendar_id ||
    event.participants?.find((p) => ['provider', 'clinicadmin'].includes(p.role))?.participant_calendar_id ||
    null;

  const handleCreateEvent = (eventResponse) => {
    if (eventResponse && editorRef.value?.insertCardAction) {
      editorRef.value.insertCardAction({
        type: 'appointment',
        id: eventResponse.event_id || '',
        title: eventResponse.event_title || 'Untitled Appointment',
        description: buildAppointmentDescription(eventResponse),
        calendarId: getAppointmentCalendarId(eventResponse),
      });
    }
  };

  const handleUpdateEvent = (eventResponse) => {
    const event = eventResponse?.event || eventResponse;
    if (event && event.event_id && editorRef.value?.updateCardAction) {
      editorRef.value.updateCardAction({
        type: 'appointment',
        id: event.event_id,
        title: event.event_title || 'Untitled Appointment',
        description: buildAppointmentDescription(event),
        calendarId: getAppointmentCalendarId(event),
        event_id: event.event_id,
        event_title: event.event_title,
        start_time: event.start_time,
        end_time: event.end_time,
        event_type: event.event_type,
        location: event.location,
        participants: event.participants,
        notes: event.notes,
        creator_calendar_id: event.creator_calendar_id,
        patient_ids: event.patient_ids,
        is_canceled: event.is_canceled,
      });
    }
  };

  const handleEditAppointment = async (eventData) => {
    await fetchCalendarDataIfNeeded();

    const rawPatientList = store.state.calendar?.rawPatientList || [];
    const patientIndex = rawPatientList.findIndex((p) => String(p.id) === String(patientId.value));
    if (patientIndex > 0) {
      const patient = rawPatientList[patientIndex];
      const reorderedList = [
        patient,
        ...rawPatientList.slice(0, patientIndex),
        ...rawPatientList.slice(patientIndex + 1),
      ];
      store.commit('calendar/SET_RAW_PATIENT_LIST', reorderedList);
    }

    store.dispatch('calendar/setCurrentEventInfo', {
      event_id: eventData.event_id,
      event_title: eventData.event_title,
      start: eventData.start_time,
      end: eventData.end_time,
      meetingType: eventData.event_type,
      location: eventData.location,
      participants: eventData.participants,
      notes: eventData.notes,
      creator_calendar_id: eventData.creator_calendar_id,
      patient_ids: eventData.patient_ids,
      mode: 'edit',
    });
    scheduleDetailActiveTab.value = 'appointment';
    showCreateAppointmentDialog.value = true;
  };

  const handleCancelAppointment = (eventData) => {
    appointmentToCancel.value = eventData;
    showCancelAppointmentDialog.value = true;
  };

  const handleCancelAppointmentDialogClose = () => {
    showCancelAppointmentDialog.value = false;
    appointmentToCancel.value = null;
  };

  const handleCancelAppointmentConfirm = async () => {
    if (!appointmentToCancel.value?.event_id) {
      handleCancelAppointmentDialogClose();
      return;
    }
    try {
      await store.dispatch('calendar/deleteEvent', {
        event_id: appointmentToCancel.value.event_id,
        is_canceled: true,
      });
      if (editorRef.value?.markCardAsCancelled) {
        editorRef.value.markCardAsCancelled({
          type: 'appointment',
          id: appointmentToCancel.value.event_id,
        });
      }
    } catch (error) {
      console.error('Failed to cancel appointment:', error);
    } finally {
      handleCancelAppointmentDialogClose();
    }
  };

  // --- Order ---
  const CHARGE_METHOD_LABELS = {
    customerPay: 'Provider Pay',
    patientPayNow: 'Patient Pay',
    patientPayLater: 'Patient Pay (Later - send email/text)',
  };

  const handleOrderSend = (orderResult) => {
    if (!orderResult?.success || !editorRef.value?.insertCardAction) return;

    const accessionId = orderResult.accessionId || '';
    const sampleId = orderResult.sampleId || '';
    const today = new Date();
    const orderDate = today.toLocaleDateString('en-US', {
      month: '2-digit', day: '2-digit', year: 'numeric',
    }).replace(/\//g, '-');
    const amount = orderResult.amount ?? 0;
    const chargeMethod = CHARGE_METHOD_LABELS[orderResult.chargeMethod] || 'N/A';

    const description = `Order Placed On: ${orderDate} | Amount: $${amount} | Payment Method: ${chargeMethod}`;

    editorRef.value.insertCardAction({
      type: 'order',
      id: accessionId,
      title: `Order ${accessionId}`,
      description,
      sampleId: String(sampleId),
      orderStatus: 'new_order',
    });

    showNewOrderDialog.value = false;
  };

  const handleSeeOrderDetails = ({ accessionId, sampleId: sid, orderData }) => {
    const sampleId = sid || orderData?.orderData?.sample_id || orderData?.sample_id || '';
    const token = sessionStorage.getItem('token');
    const portalBaseUrl = process.env.VUE_APP_ENV === 'production'
      ? 'https://portal.vibrant-wellness.com'
      : 'https://staging.va-portal.pages.dev';
    const url = `${portalBaseUrl}/#/patient-profile/${patientId.value}/${accessionId}/${sampleId}?jwtToken=${token}`;
    window.open(url, '_blank');
  };

  const handleSeeFormDetails = () => {
    router.push('/my-calendar');
  };

  // --- Document CRUD ---
  const handleSendDocuments = async (selectedDocuments) => {
    if (!editorRef.value?.insertCardAction) return;
    for (const doc of selectedDocuments) {
      if (doc.type === 'form_template') {
        try {
          const response = await sendFormApi(doc.id, {
            patient_id: parseInt(patientId.value),
          });
          const assignment = response?.data;
          if (assignment?.id) {
            editorRef.value.insertCardAction({
              type: 'form',
              id: assignment.id,
              title: doc.name || doc.title || 'Please complete the form',
              description: '',
              dueDate: assignment.due_date || '',
              isCompleted: false,
              formDescription: 'This is a form completion task for patient.',
            });
          }
        } catch (error) {
          console.error('Failed to send form template:', error);
        }
        continue;
      }
      editorRef.value.insertCardAction({
        type: 'document',
        id: doc.id,
        title: doc.fileName || 'Untitled Document',
        description: formatDocumentCardDescription(doc),
      });
    }
  };

  const handlePreviewDocument = async (docData) => {
    const fileId = docData?.id;
    if (!fileId) return;
    try {
      const response = await downloadPatientFileApi(fileId);
      if (response.data instanceof Blob) {
        const blobUrl = URL.createObjectURL(response.data);
        window.open(blobUrl, '_blank');
      }
    } catch (error) {
      console.error('Error previewing document:', error);
    }
  };

  const handleDeleteDocument = (docData) => {
    documentToDelete.value = docData;
    showDeleteDocumentDialog.value = true;
  };

  const handleDeleteDocumentDialogClose = () => {
    showDeleteDocumentDialog.value = false;
    documentToDelete.value = null;
  };

  const handleDeleteDocumentConfirm = async () => {
    if (!documentToDelete.value?.id) {
      handleDeleteDocumentDialogClose();
      return;
    }
    try {
      await deletePatientFileApi(documentToDelete.value.id);
      if (editorRef.value?.removeCardAction) {
        editorRef.value.removeCardAction({
          type: 'document',
          id: documentToDelete.value.id,
        });
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      handleDeleteDocumentDialogClose();
    }
  };

  return {
    // Dialog state
    showCreateAppointmentDialog,
    showSendDocumentDialog,
    showNewOrderDialog,
    showDeleteTaskDialog,
    showCancelAppointmentDialog,
    showDeleteDocumentDialog,
    scheduleDetailActiveTab,
    clinicId,

    // Slash commands
    handleSlashCommand,

    // Appointment dialog
    handleAppointmentDialogClose,

    // Task
    handleCreateTask,
    handleUpdateTask,
    handleEditTask,
    handleDeleteTask,
    handleDeleteTaskDialogClose,
    handleDeleteTaskConfirm,

    // Appointment
    handleCreateEvent,
    handleUpdateEvent,
    handleEditAppointment,
    handleCancelAppointment,
    handleCancelAppointmentDialogClose,
    handleCancelAppointmentConfirm,

    // Order
    initialOrderItems,
    handleOrderSend,
    handleSeeOrderDetails,
    handleSeeFormDetails,

    // Document
    handleSendDocuments,
    handlePreviewDocument,
    handleDeleteDocument,
    handleDeleteDocumentDialogClose,
    handleDeleteDocumentConfirm,
  };
}
